import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { USER_LOGOUT } from '../../admin_module/constants/userConstants'
import BACKEND_URL from '../../backendUrl'
import Loader from './Loader'
import Message from './Message'

const ExportFilesByName = ({ studyId, onToast }) => {
  const dispatch = useDispatch()
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const [show, setShow] = useState(false)
  const [filenamesText, setFilenamesText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasPhotoUpload, setHasPhotoUpload] = useState(false)
  const [checkingPhotoUpload, setCheckingPhotoUpload] = useState(true)

  useEffect(() => {
    let cancelled = false

    const checkPhotoUpload = async () => {
      const now = new Date()
      if (!(userInfo && userInfo.expires > now && !logout)) {
        setCheckingPhotoUpload(false)
        return
      }

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
        const { data } = await axios.get(
          BACKEND_URL + `/api/data/${studyId}/has_photo_upload_resp`,
          config
        )
        if (!cancelled) {
          setHasPhotoUpload(Boolean(data.hasPhotoUpload))
        }
      } catch (err) {
        if (!cancelled) {
          setHasPhotoUpload(false)
          if (err.response && err.response.status === 401) {
            dispatch({ type: USER_LOGOUT })
            localStorage.removeItem('adminModuleUserInfo')
          }
        }
      } finally {
        if (!cancelled) {
          setCheckingPhotoUpload(false)
        }
      }
    }

    checkPhotoUpload()
    return () => {
      cancelled = true
    }
  }, [studyId, userInfo, logout, dispatch])

  const handleClose = () => {
    if (loading) return
    setShow(false)
    setError('')
    setFilenamesText('')
  }

  const handleExport = async () => {
    const now = new Date()
    if (!(userInfo && userInfo.expires > now && !logout)) {
      dispatch({ type: USER_LOGOUT })
      localStorage.removeItem('adminModuleUserInfo')
      return
    }

    const filenames = filenamesText
      .split(/[\n,]+/)
      .map((s) =>
        s
          .replace(/\u00A0/g, ' ')
          .trim()
          .replace(/^["']+|["']+$/g, '')
          .trim()
      )
      .filter(Boolean)

    if (filenames.length === 0) {
      setError('Please enter at least one filename')
      return
    }

    setLoading(true)
    setError('')

    try {
      const config = {
        timeout: 300000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.post(
        BACKEND_URL + `/api/data/${studyId}/download_files_by_names`,
        { filenames },
        config
      )

      const mime =
        data.contentType ||
        (data.title && data.title.endsWith('.zip')
          ? 'application/zip'
          : 'application/octet-stream')
      const file_path = `data:${mime};base64,` + data.content
      const a = document.createElement('A')
      a.href = file_path
      a.download = data.title
      document.body.appendChild(a)
      setTimeout(() => {
        a.click()
        document.body.removeChild(a)
      }, 100)

      if (data.notFound && data.notFound.length > 0 && onToast) {
        onToast({
          variant: 'danger',
          message: 'Some files were not found: ' + data.notFound.join(', '),
        })
      }

      setShow(false)
      setFilenamesText('')
    } catch (err) {
      if (err.response && err.response.status === 401) {
        dispatch({ type: USER_LOGOUT })
        localStorage.removeItem('adminModuleUserInfo')
        return
      }
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        err.message ||
        'Failed to export files'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  if (checkingPhotoUpload || !hasPhotoUpload) {
    return null
  }

  return (
    <>
      <Button
        variant='primary'
        className='btn-sm m-1'
        onClick={() => setShow(true)}
      >
        Export Image Files
      </Button>

      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Export Image Files by Filename</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Message variant='danger'>{error}</Message>}
          {loading && <Loader />}
          <Form.Group controlId={`exportFilesFilenames-${studyId}`}>
            <Form.Label>
              Enter one or more filenames (one per line or comma-separated).
              Filenames should match the photo column from the text export.
            </Form.Label>
            <Form.Control
              as='textarea'
              rows={6}
              value={filenamesText}
              disabled={loading}
              onChange={(e) => setFilenamesText(e.target.value)}
              placeholder={'filename1.jpg\nfilename2.jpg'}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose} disabled={loading}>
            Close
          </Button>
          <Button variant='primary' onClick={handleExport} disabled={loading}>
            Export
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ExportFilesByName
